// WhatsApp Flow lifecycle state, derived from the fields the list endpoint
// already returns. Shown in the detail pane's Flow tab label so Flow health is
// visible without opening the tab.

import { Chatbot } from '@/lib/sdk/chatbot/types';

export interface FlowState {
  label: string;
  colorScheme: string;
}

export function getFlowState(chatbot: Chatbot): FlowState | null {
  if (!chatbot.flows_enabled) return null;
  if (!chatbot.flow_id) return { label: 'Not synced', colorScheme: 'gray' };
  const version = chatbot.flow_version ?? 1;
  if (chatbot.flow_published) return { label: `Published v${version}`, colorScheme: 'green' };
  return { label: `Draft v${version}`, colorScheme: 'blue' };
}

/** Matches platform.FlowLaunchCooldown: how long launches run in chat after Meta refuses a Flow. */
const FLOW_LAUNCH_COOLDOWN_MINUTES = 30;

/**
 * Why a Flows-enabled workflow is running in chat instead of its Flow, in plain words for
 * the Settings tab. Empty when nothing stands in the way.
 */
export function flowChatNotes(chatbot: Chatbot, now: Date = new Date()): string[] {
  if (!chatbot.flows_enabled) return [];
  const notes: string[] = [];
  if (chatbot.flow_ineligible_reason) {
    const reason = chatbot.flow_ineligible_reason.replace(/^flow generator: /, '');
    notes.push(`Runs in chat: this task list can't be shown as a Flow. ${reason}`);
  }
  if (chatbot.flow_launch_failed_at) {
    const until = new Date(
      new Date(chatbot.flow_launch_failed_at).getTime() + FLOW_LAUNCH_COOLDOWN_MINUTES * 60_000
    );
    if (until > now) {
      notes.push(
        `Runs in chat until ${until.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}: WhatsApp refused to open this Flow.`
      );
    }
  }
  if (chatbot.flow_out_of_date) {
    notes.push(
      chatbot.flow_hold_draft && chatbot.flow_published
        ? 'Runs in chat: changes saved while the Flow is held can’t reach the live Flow. Switch off Hold in draft to publish them.'
        : 'Runs in chat until the Flow syncs your latest changes, usually within a minute.'
    );
  }
  return notes;
}

/** One-line reminder summary for the table's Reminders column. */
export function reminderSummary(chatbot: Chatbot): string {
  const count = chatbot.reminders?.length ?? 0;
  if (count === 0) return 'None';
  if (count === 1) return `Daily · ${chatbot.reminders[0].nudge_time}`;
  return `${count} daily reminders`;
}

/** `/start CODE` — the command a user sends on WhatsApp to open the workflow. */
export function startCommand(shortCode: string): string {
  return `/start ${shortCode}`;
}

/** Deep link, falling back to a wa.me URL when the bot has no generated QR yet. */
export function deepLink(chatbot: Chatbot): string {
  if (chatbot.qr_code?.deep_link_url) return chatbot.qr_code.deep_link_url;
  const number = import.meta.env.VITE_APP_WHATSAPP_NUMBER;
  return `https://wa.me/${number}?text=${encodeURIComponent(startCommand(chatbot.short_code))}`;
}
